/**
 * Session store (Zustand + persist): user-facing settings and the local autosave buffer.
 *
 * The XState actor's persisted snapshot is written here on every transition (see game/useGame.ts),
 * giving instant local resume that survives refresh/offline. The backend sync layer (added with the
 * API) will additionally push the snapshot to the server and reconcile on load. See docs/architecture.md.
 */
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export interface Settings {
  soundOn: boolean;
  /** null = follow the OS prefers-reduced-motion setting. */
  reducedMotion: boolean | null;
  highContrast: boolean;
}

interface SessionState {
  runId: string | null;
  /** XState persisted snapshot of the in-progress (or just-finished) run; null = no run. */
  runSnapshot: unknown | null;
  runFinished: boolean;
  settings: Settings;
  startRun: () => string;
  saveSnapshot: (snapshot: unknown, finished: boolean) => void;
  clearRun: () => void;
  setSettings: (patch: Partial<Settings>) => void;
}

export const useSession = create<SessionState>()(
  persist(
    (set, get) => ({
      runId: null,
      runSnapshot: null,
      runFinished: false,
      settings: { soundOn: true, reducedMotion: null, highContrast: false },
      startRun: () => {
        const runId = crypto.randomUUID();
        set({ runId, runSnapshot: null, runFinished: false });
        return runId;
      },
      saveSnapshot: (snapshot, finished) => set({ runSnapshot: snapshot, runFinished: finished }),
      clearRun: () => set({ runId: null, runSnapshot: null, runFinished: false }),
      setSettings: (patch) => set({ settings: { ...get().settings, ...patch } }),
    }),
    {
      name: "patc:session:v1",
      storage: createJSONStorage(() => localStorage),
      version: 1,
      partialize: (s) => ({
        runId: s.runId,
        runSnapshot: s.runSnapshot,
        runFinished: s.runFinished,
        settings: s.settings,
      }),
    },
  ),
);
